import Image from 'next/image'
import type { GetServerSideProps } from 'next'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import type { Locale } from '../locales/messages'
import { extractLocaleFromCookie } from '../lib/locale'
import { Skeleton } from '../components/ui/Skeleton'

type RequestState = 'idle' | 'loading' | 'success'

const DEV_MODE = process.env.NODE_ENV !== 'production'
const GOOGLE_REDIRECT_TARGET = encodeURIComponent('/app')
const GOOGLE_AUTH_URL = `/api/auth/oauth/google?redirect=${GOOGLE_REDIRECT_TARGET}`

function readErrorMessage(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== 'object') return fallback
  const detail = (payload as { detail?: string }).detail
  if (detail && typeof detail === 'string') return detail
  const error = (payload as { error?: string }).error
  if (error && typeof error === 'string') return error
  return fallback
}

export function LoginSkeleton() {
  return (
    <main className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="flex flex-col items-center text-center">
          <Skeleton className="h-10 w-32 rounded-full" />
          <Skeleton className="mt-6 h-7 w-32 rounded-full" />
          <Skeleton className="mt-2 h-4 w-56 rounded-full" />
        </div>
        <Skeleton className="mt-6 h-14 w-full rounded-2xl" />
        <Skeleton className="mt-4 h-11 w-full rounded-full" />
        <Skeleton className="mt-6 h-11 w-full rounded-full" />
        <Skeleton className="mt-3 h-11 w-full rounded-full" />
      </div>
    </main>
  )
}

export default function Login() {
  const [mounted, setMounted] = useState(false)
  const [email, setEmail] = useState('')
  const [state, setState] = useState<RequestState>('idle')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [devBusy, setDevBusy] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return <LoginSkeleton />

  const trimmedEmail = email.trim()
  const disableSend = state === 'loading' || !trimmedEmail

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="flex flex-col items-center text-center">
          <Link href="/" aria-label="Lernio home" className="inline-flex items-center justify-center">
            <Image src="/logo.png" alt="Lernio" width={140} height={40} className="h-9 w-auto" priority />
          </Link>
          <h1 className="mt-6 text-2xl font-semibold text-slate-900">Sign in</h1>
          <p className="mt-2 text-sm text-slate-600">Enter your email to receive a one-time code.</p>
        </div>

        <form
          className="mt-6 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!trimmedEmail) return
            setState('loading')
            setMessage('')
            setError('')

            const payload = { email: trimmedEmail, redirect: '/app' }
            const attempt = async (url: string) => {
              const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              })
              if (response.status === 404) return false
              const data = await response.json().catch(() => ({}))
              if (!response.ok) {
                throw new Error(readErrorMessage(data, 'Unable to send magic code. Please try again.'))
              }
              return true
            }

            try {
              const viaMagicLink = await attempt('/api/auth/magic-link')
              if (!viaMagicLink) {
                const fallback = await attempt('/api/auth/magic/request')
                if (!fallback) {
                  throw new Error('Unable to send magic code. Please try again.')
                }
              }
              setState('success')
              setMessage('Check your email for a sign-in link or 6-character code to continue.')
            } catch (err: any) {
              setState('idle')
              setError(err?.message || 'Unable to send magic code. Please try again.')
            }
          }}
        >
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200"
              placeholder="you@example.com"
              required
            />
          </div>
          <button
            type="submit"
            disabled={disableSend}
            className="w-full rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-60"
          >
            {state === 'loading' ? 'Sending magic code…' : state === 'success' ? 'Send again' : 'Send Code'}
          </button>
        </form>

        {message && <p className="mt-4 text-sm text-slate-600">{message}</p>}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <div className="relative my-6 text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
          <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-slate-200" aria-hidden />
          <span className="relative bg-white px-3">or</span>
        </div>

        <button
          type="button"
          onClick={() => {
            window.location.href = GOOGLE_AUTH_URL
          }}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-cyan-400 hover:text-slate-900"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        {DEV_MODE && (
          <button
            type="button"
            onClick={async () => {
              if (!trimmedEmail) {
                setError('Enter an email before using the dev login.')
                return
              }
              setDevBusy(true)
              setMessage('')
              setError('')
              try {
                const response = await fetch('/api/auth/dev-login', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: trimmedEmail }),
                })
                const data = await response.json().catch(() => ({}))
                if (!response.ok) {
                  throw new Error(readErrorMessage(data, 'Unable to complete dev login.'))
                }
                window.location.href = '/app'
              } catch (err: any) {
                setError(err?.message || 'Unable to complete dev login.')
              } finally {
                setDevBusy(false)
              }
            }}
            disabled={devBusy}
            className="mt-3 w-full rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-cyan-400 disabled:pointer-events-none disabled:opacity-60"
          >
            {devBusy ? 'Signing in…' : 'Use dev login'}
          </button>
        )}
      </div>
    </main>
  )
}

export const getServerSideProps: GetServerSideProps<{ locale?: Locale }> = async ({ req }) => {
  const locale = extractLocaleFromCookie(req.headers?.cookie)
  const token = (req as any).cookies?.token
  if (token) {
    return { redirect: { destination: '/app', permanent: false } }
  }
  return { props: { locale: locale ?? 'en' } }
}

function GoogleIcon() {
  return (
    <svg
      aria-hidden
      className="h-4 w-4"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
    >
      <path
        fill="#EA4335"
        d="M5.266 12.278a6.77 6.77 0 0 1 .352-2.144L2.353 7.64A11.107 11.107 0 0 0 1 12.278c0 1.77.424 3.44 1.173 4.918l3.267-2.529a6.724 6.724 0 0 1-.174-2.389"
      />
      <path
        fill="#34A853"
        d="M12.284 5.557c1.407 0 2.68.485 3.678 1.432l2.762-2.762C16.949 2.292 14.847 1.5 12.284 1.5 7.785 1.5 3.942 4.163 2.353 7.64l3.265 2.494c.754-2.26 2.9-4.577 6.666-4.577"
      />
      <path
        fill="#4A90E2"
        d="M12.284 22.5c3.042 0 5.598-1.004 7.464-2.734l-3.445-2.707c-.928.63-2.178 1.08-4.019 1.08-3.102 0-5.735-2.094-6.677-4.997L2.34 17.203c1.577 3.507 4.975 5.297 9.944 5.297"
      />
      <path
        fill="#FBBC05"
        d="M21.5 12.278c0-.653-.1-1.36-.248-2.014h-8.968v3.814h5.131c-.261 1.25-.952 2.227-1.928 2.885l3.445 2.707c2.01-1.856 3.568-4.608 3.568-7.392"
      />
    </svg>
  )
}
